import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { LabelButton } from "./blent-component/Button";
import { SideSheetModal } from "./blent-component/SideSheet";
import { PropertiesSetter } from "./component/PropertiesSetter";
import { CardViewer } from "./component/CardViewer";
import { ImgSetter } from "./component/ImgSetter";
import { LanguageSwitcher } from "./component/LanguageSwitcher";
import {
  JobPropertiesViewerProps,
  ImgPropertiesViewerProps,
  GradientType,
} from "./types/globals.types";
import styles from "./app-styles.module.scss";
import "./gradient.scss";

const TITLE_MAX_LENGTH = 27;
const REGION_MAX_LENGTH = 20;

const App = () => {
  const [imgSetterVisible, setImgSetterVisible] = useState<boolean>(false);
  const defaultGradient: GradientType = "yellow";
  const [gradient, setGradient] = useState<GradientType>(defaultGradient);
  const [img, setImg] = useState<ImgPropertiesViewerProps>({
    image: undefined,
    logoName: "logo_corporate_white_v1_0.png",
    withLogo: true,
    withSlogan: true,
    withGradient: true,
  });
  const [job, setJob] = useState<JobPropertiesViewerProps>({
    jobFunctionFromList: [],
    jobFunctionManually: "",
    jobTitle: "",
    jobRegion: "",
    jobSeniorityFromList: [],
    jobSeniorityManually: "",
    jobWorkSetUpFromList: [],
    jobWorkSetUpManually: "",
    jobLocationFromList: [],
    jobLocationManually: "",
    jobSummary: "",
    slogan: [],
  });
  const { t, i18n } = useTranslation();

  useEffect(() => {
    //Use last langage if one saved
    const savedLanguage = localStorage.getItem("selectedLanguage");
    i18n.changeLanguage(savedLanguage ?? "en");
  }, [i18n]);

  useEffect(() => {
    document.documentElement.lang = i18n.language;
  }, [i18n.language]);

  return (
    <div className={styles.appContainerWrapper}>
      <div className={styles.appContainer}>
        <div className={styles.left}>
          <div className={styles.langueSwitcherContainer}>
            <LanguageSwitcher />
          </div>
          <PropertiesSetter
            {...job}
            setJobFunctionFromList={(v) =>
              setJob((prev) => ({ ...prev, jobFunctionFromList: v }))
            }
            setJobFunctionManually={(v) =>
              setJob((prev) => ({ ...prev, jobFunctionManually: v }))
            }
            setJobTitle={(v) => {
              if (v.length <= TITLE_MAX_LENGTH)
                setJob((prev) => ({ ...prev, jobTitle: v }));
            }}
            setJobRegion={(v) => {
              if (v.length <= REGION_MAX_LENGTH)
                setJob((prev) => ({ ...prev, jobRegion: v }));
            }}
            //
            setJobSeniorityFromList={(v) =>
              setJob((prev) => ({ ...prev, jobSeniorityFromList: v }))
            }
            setJobSeniorityManually={(v) =>
              setJob((prev) => ({ ...prev, jobSeniorityManually: v }))
            }
            //
            setJobWorkSetUpFromList={(v) =>
              setJob((prev) => ({ ...prev, jobWorkSetUpFromList: v }))
            }
            setJobWorkSetUpManually={(v: string) =>
              setJob((prev) => ({ ...prev, jobWorkSetUpManually: v }))
            }
            //
            setJobLocationFromList={(v) =>
              setJob((prev) => ({ ...prev, jobLocationFromList: v }))
            }
            setJobLocationManually={(v: string) =>
              setJob((prev) => ({ ...prev, jobLocationManually: v }))
            }
            setJobSummary={(v) =>
              setJob((prev) => ({ ...prev, jobSummary: v }))
            }
            setSlogan={(v) => setJob((prev) => ({ ...prev, slogan: v }))}
            gradient={gradient}
            setGradient={setGradient}
          />
          <LabelButton
            corners="squared"
            size="m"
            label={t("selectImage")}
            auto
            onClick={(e) => {
              e.preventDefault();
              setImgSetterVisible(true);
            }}
            variant="secondary"
          />
          <SideSheetModal
            // scrollableContent={false}
            open={imgSetterVisible}
            closeMe={() => setImgSetterVisible(false)}
            fullscreen
            position="left"
          >
            {imgSetterVisible && (
              <ImgSetter
                {...img}
                gradient={gradient}
                setWithGradient={(v) => setImg({ ...img, withGradient: v })}
                setImage={(v) => setImg({ ...img, image: v })}
                setLogoName={(v) => {
                  setImg({ ...img, logoName: v });
                }}
                setWithLogo={(v) => setImg({ ...img, withLogo: v })}
                setWithSlogan={(v) => setImg({ ...img, withSlogan: v })}
                closeMe={() => setImgSetterVisible(false)}
              />
            )}
          </SideSheetModal>
        </div>
        <div className={styles.right}>
          <CardViewer job={job} img={img} gradient={gradient} />
        </div>
      </div>
    </div>
  );
};

export default App;
